// What one token is to another: the KIND of an attachment.
//
// Not to be confused with TOKEN_ROLES in riders.ts, which says what a token IS
// on the map (creature, mount, walks-over-others, emanation). A role is a
// property of the node; a kind is a property of the edge. `mount` reads in both
// senses, which is exactly why the picker felt muddled — see
// docs/TOKEN-ATTACHMENTS.md.
//
// Deliberately a LABEL for now. Several of these have real SRD mechanics behind
// them (swallowed is blinded + restrained, total cover, damage at the
// swallower's turn start) and the plan is for a mechanical kind to emit an
// EffectDef and let the audited effects engine run it. None of that happens
// here: choosing a kind records what the link is and changes only whether the
// follower can be dragged loose.
//
// Kept out of the tracker component on purpose, so the Compass canvas can one
// day enforce the same vocabulary without lifting it out of some JSX.

export type AttachmentKind = "mount" | "climbed" | "swallowed" | "engulfed" | "grappled" | "carried" | "art" | "misc";

export interface AttachmentDef {
  value: AttachmentKind;
  label: string;
  /** Cannot simply be dragged clear: the follower stays until something releases it. */
  sticky: boolean;
  hint: string;
}

export const ATTACHMENT_KINDS: AttachmentDef[] = [
  { value: "mount", label: "rides", sticky: false, hint: "A ridden mount. Drag the rider clear to dismount; drop it back on to re-mount." },
  { value: "climbed", label: "climbed on", sticky: false, hint: "Clambered onto something that is not a mount. Comes off the same way, and is thrown when the base falls prone." },
  { value: "swallowed", label: "swallowed", sticky: true, hint: "Inside it. In the SRD the target is blinded and restrained, has total cover from outside, and takes damage at the start of the swallower's turns." },
  { value: "engulfed", label: "engulfed", sticky: true, hint: "Enveloped, as by a gelatinous cube: restrained and unable to breathe while it lasts." },
  { value: "grappled", label: "grappled", sticky: true, hint: "Held. Speed 0, and it moves where the grappler drags it until it escapes." },
  { value: "carried", label: "carried", sticky: true, hint: "Picked up and carried: an unconscious ally, a body, a chest. No say in where it goes." },
  { value: "art", label: "attached art", sticky: true, hint: "Not a creature at all — spell art, a marker, a light source. Follows and never comes loose." },
  { value: "misc", label: "other", sticky: false, hint: "Something else that should move together. A label, nothing more." },
];

const BY_VALUE = new Map(ATTACHMENT_KINDS.map((k) => [k.value, k]));

/** The definition for a kind, or null for "not attached" and for anything unknown. */
export const attachmentKind = (v: string | undefined | null): AttachmentDef | null => (v ? BY_VALUE.get(v as AttachmentKind) || null : null);

/** What the DM sees on a row: the kind's label, or a dash when nothing is attached. */
export const attachmentLabel = (v: string | undefined | null): string => attachmentKind(v)?.label || "—";

/**
 * Whether a follower of this kind may be dragged loose.
 *
 * This is the one behaviour a kind changes today, and it maps onto the `attached`
 * flag the follow engine already honours: art and effects follow and never
 * dismount. A swallowed creature should not slide out because the DM nudged it,
 * so the sticky kinds take the same path — the difference between "climbed on"
 * and "swallowed" is exactly whether dragging it away means anything.
 */
export const kindIsSticky = (v: string | undefined | null): boolean => Boolean(attachmentKind(v)?.sticky);

/** A kind for a follower the DM has not described, so old links keep working. */
export const DEFAULT_KIND: AttachmentKind = "misc";
